import { useState, useEffect } from 'react';
import {
  type EDModeState, type EDCheckin, type EDVisit,
  type PainArea, type Medication,
} from '../appTypes';
import type { PainIntensity } from '../constants/painConstants';
import { INTENSITY_LABELS } from '../constants/painConstants';
import type { EDContentItem, EDStageId } from '../edContent';
import { getRecommendations } from '../edContent';
import { generateEDReportPDF } from '../generateEDReportPDF';
import { apiPost, apiPut, apiGet } from '../appUtils';
import { SK_ED_HISTORY } from '../constants/storageKeys';
import { BODY_AREA_NAMES } from '../appConstants';
import { toast } from 'sonner';

/** How long the "check-in saved" toast shows (ms) — mirrors PDF_TOAST_MS in App */
const PDF_TOAST_MS = 3000;
/** Interval between automatic ED check-in prompts (ms) */
const ED_TIMER_INTERVAL_MS = 20 * 60 * 1000;
/** Clipboard copy success duration (ms) — mirrors CHECKIN_DELAY_MS in App */
const CHECKIN_DELAY_MS = 2000;

interface UseEDModeParams {
  authToken: string | null;
  medications: Medication[];
  earnXP: (amount: number) => void;
}

export function useEDMode({ authToken, medications, earnXP }: UseEDModeParams) {
  // --- Core ED mode state ---
  const [edModeState, setEdModeState] = useState<EDModeState>('off');
  const [edTimerID, setEdTimerID] = useState<ReturnType<typeof setInterval> | null>(null);
  const [edCheckins, setEdCheckins] = useState<EDCheckin[]>([]);
  const [currentEDVisitId, setCurrentEDVisitId] = useState<string | null>(null);
  const [edVisitHistory, setEdVisitHistory] = useState<EDVisit[]>([]);

  // --- Script & report ---
  const [edScript, setEdScript] = useState<string>(
    "I have sickle cell disease and I'm having a pain crisis. My baseline pain management includes hydromorphone 2mg every 3 hours. My pain is currently 9/10. I've tried heat, rest, and my home pain meds for 6 hours without relief. I need appropriate pain management as outlined in my care plan with Dr. Williams."
  );
  const [edReportNote, setEdReportNote] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // --- ED check-in flow state ---
  const [edCheckinStep, setEdCheckinStep] = useState(1);
  const [edVocStatus, setEdVocStatus] = useState('');
  const [edPainLevel, setEdPainLevel] = useState(5);
  const [edBodyAreas, setEdBodyAreas] = useState<Record<string, PainIntensity>>({});
  const [edSafetyFlags, setEdSafetyFlags] = useState<string[]>([]);
  const [edCheckinVisible, setEdCheckinVisible] = useState(false);
  const [edCheckinComplete, setEdCheckinComplete] = useState(false);

  // --- ED check-in summary ---
  type EDCheckinSummary = {
    painLevel: number;
    vocStatus: string;
    bodyAreas: PainArea[];
    safetyFlags: string[];
  };
  const [edCheckinSummary, setEdCheckinSummary] = useState<EDCheckinSummary | null>(null);

  // --- Stage & recommendations ---
  const [edStage, setEdStage] = useState<EDStageId | null>(null);
  const [checkinRecommendations, setCheckinRecommendations] = useState<EDContentItem[]>([]);

  // --- Effects ---

  // Clear ED check-in summary when leaving active mode
  useEffect(() => {
    if (edModeState !== 'active') {
      setEdCheckinSummary(null);
    }
  }, [edModeState]);

  // --- Persistence helpers ---

  function saveEDHistory(history: EDVisit[]) {
    try {
      localStorage.setItem(SK_ED_HISTORY, JSON.stringify(history));
    } catch { /* localStorage may be unavailable */ }
  }

  async function loadEDHistory() {
    try {
      const stored = localStorage.getItem(SK_ED_HISTORY);
      if (stored) {
        const parsed: EDVisit[] = JSON.parse(stored);
        setEdVisitHistory(parsed);
      }
    } catch { /* error handled silently */ }
    const remote = await apiGet<EDVisit[]>('/ed/visits', authToken);
    if (remote && remote.length > 0) {
      setEdVisitHistory(remote);
      saveEDHistory(remote);
    }
  }

  // --- Visit lifecycle ---

  function startEDVisit() {
    const id = 'visit-' + Date.now();
    setCurrentEDVisitId(id);
    setEdCheckins([]);
    const visit: EDVisit = { id, startTime: new Date().toISOString(), checkins: [] };
    setEdVisitHistory(prev => {
      const updated = [...prev, visit];
      saveEDHistory(updated);
      return updated;
    });
    apiPost('/ed/visits', { id, startTime: visit.startTime }, authToken).catch(() => {});
    return id;
  }

  function endEDVisit() {
    if (!currentEDVisitId) return;
    const visitId = currentEDVisitId;
    setEdVisitHistory(prev => {
      const updated = prev.map(v =>
        v.id === visitId ? { ...v, endTime: new Date().toISOString() } : v
      );
      saveEDHistory(updated);
      return updated;
    });
    setCurrentEDVisitId(null);
    apiPut(`/ed/visits/${visitId}/end`, null, authToken);
  }

  function logEDCheckin(checkin: Omit<EDCheckin, 'id' | 'timestamp' | 'visitId'>) {
    const id = 'checkin-' + Date.now();
    const visitId = currentEDVisitId || 'no-visit';
    const newCheckin: EDCheckin = { id, timestamp: new Date().toISOString(), visitId, ...checkin };
    setEdCheckins(prev => [...prev, newCheckin]);
    if (currentEDVisitId) {
      setEdVisitHistory(prev => {
        const updated = prev.map(v =>
          v.id === currentEDVisitId ? { ...v, checkins: [...v.checkins, newCheckin] } : v
        );
        saveEDHistory(updated);
        return updated;
      });
      apiPost(`/ed/visits/${currentEDVisitId}/checkins`, {
        id: newCheckin.id,
        timestamp: newCheckin.timestamp,
        painLevel: newCheckin.painLevel,
        vocStatus: newCheckin.vocStatus,
        bodyAreas: newCheckin.bodyAreas,
        safetyFlags: newCheckin.safetyFlags,
      }, authToken).catch(() => {});
    }
    return newCheckin;
  }

  function clearEDCheckins() {
    setEdCheckins([]);
  }

  // --- Timer ---

  function startEDTimer(mode?: EDModeState) {
    if (edTimerID) clearInterval(edTimerID);
    const id = setInterval(() => {
      if (edModeState === 'active' || mode === 'active') {
        setEdCheckinVisible(true);
      }
    }, ED_TIMER_INTERVAL_MS);
    setEdTimerID(id);
  }

  function stopEDTimer() {
    if (edTimerID) {
      clearInterval(edTimerID);
      setEdTimerID(null);
    }
  }

  // --- Mode cycling ---

  function cycleEDMode() {
    const order: EDModeState[] = ['off', 'plan', 'active', 'reflect'];
    setEdModeState(prev => {
      const idx = order.indexOf(prev);
      const next = order[(idx + 1) % order.length];

      if (prev === 'active') {
        stopEDTimer();
        endEDVisit();
        clearEDCheckins();
        setEdCheckinVisible(false);
        setEdCheckinComplete(false);
        setEdCheckinSummary(null);
      }
      if (next === 'active') {
        startEDVisit();
        startEDTimer(next);
      }
      if (next === 'off') {
        clearEDCheckins();
        setEdCheckinSummary(null);
      }
      return next;
    });
  }

  // --- Body area & safety flag toggles ---

  function toggleEdBodyArea(area: string) {
    setEdBodyAreas(prev => {
      const current = prev[area];
      if (!current) return { ...prev, [area]: 1 as PainIntensity };
      if (current === 1) return { ...prev, [area]: 2 as PainIntensity };
      if (current === 2) return { ...prev, [area]: 3 as PainIntensity };
      const next = { ...prev };
      delete next[area];
      return next;
    });
  }

  function toggleSafetyFlag(flag: string) {
    setEdSafetyFlags(prev =>
      prev.includes(flag) ? prev.filter(f => f !== flag) : [...prev, flag]
    );
  }

  // --- Submit ED check-in ---

  function submitEDCheckin() {
    const bodyAreas: PainArea[] = Object.entries(edBodyAreas).map(([area, intensity]) => ({ area, intensity }));
    logEDCheckin({
      painLevel: edPainLevel,
      vocStatus: edVocStatus,
      bodyAreas,
      safetyFlags: edSafetyFlags,
    });
    const recs = getRecommendations(10 - edPainLevel, edSafetyFlags, 2);
    setCheckinRecommendations(recs);
    earnXP(75);
    setEdCheckinSummary({
      painLevel: edPainLevel,
      vocStatus: edVocStatus,
      bodyAreas,
      safetyFlags: [...edSafetyFlags],
    });
    setEdCheckinComplete(true);
    setEdCheckinVisible(false);
    setEdCheckinStep(1);
    setEdVocStatus('');
    setEdBodyAreas({});
    setEdSafetyFlags([]);
    setTimeout(() => setEdCheckinComplete(false), PDF_TOAST_MS);
  }

  // --- Report: copy & PDF ---

  function copyEDReport() {
    const lines: string[] = [];
    lines.push('=== CRISIS ADVOCACY SCRIPT ===');
    lines.push(edScript);
    lines.push('');
    const activeMeds = medications.filter(m => m.active);
    if (activeMeds.length > 0) {
      lines.push('=== MY CURRENT MEDICATIONS ===');
      activeMeds.forEach(med => {
        lines.push(`• ${med.name} — ${med.dose} — ${med.frequency}${med.notes ? ` (${med.notes})` : ''}`);
      });
      lines.push('');
    }
    lines.push("=== TODAY'S CRISIS SNAPSHOTS ===");
    if (edCheckins.length === 0) {
      lines.push('No check-ins logged yet during this visit.');
    } else {
      edCheckins.forEach((c, i) => {
        const t = new Date(c.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        lines.push(`\n[${i + 1}] ${t}`);
        lines.push(`VOC Status: ${c.vocStatus || 'Not specified'}`);
        lines.push(`Pain level: ${c.painLevel}/10`);
        if (c.bodyAreas.length > 0) {
          lines.push(`Body areas: ${c.bodyAreas.map(a => `${BODY_AREA_NAMES[a.area] || a.area} (${INTENSITY_LABELS[a.intensity]})`).join(', ')}`);
        }
        if (c.safetyFlags.length > 0) {
          lines.push(`Safety flags: ${c.safetyFlags.join(', ')}`);
        }
      });
    }
    if (edReportNote.trim()) {
      lines.push('');
      lines.push('=== WHAT I WANT THIS TEAM TO KNOW ===');
      lines.push(edReportNote);
    }
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), CHECKIN_DELAY_MS);
    }).catch(() => {
      toast.error("Couldn't copy to clipboard.");
    });
  }

  async function downloadEDReportPDF() {
    setPdfLoading(true);
    try {
      await generateEDReportPDF({
        edScript,
        edCheckins,
        edReportNote,
        bodyAreaNames: BODY_AREA_NAMES,
      });
    } catch {
      toast.error("Couldn't build the report. Try again.");
    } finally {
      setPdfLoading(false);
    }
  }

  function copyScript(type: 'ed') {
    if (type === 'ed') {
      navigator.clipboard.writeText(edScript).then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), CHECKIN_DELAY_MS);
      }).catch(() => {
        toast.error("Couldn't copy to clipboard.");
      });
    }
  }

  return {
    // Core state
    edModeState, setEdModeState,
    edCheckins, setEdCheckins,
    currentEDVisitId,
    edVisitHistory, setEdVisitHistory,
    edScript, setEdScript,
    edReportNote, setEdReportNote,
    copySuccess,
    pdfLoading,

    // Check-in flow state
    edCheckinStep, setEdCheckinStep,
    edVocStatus, setEdVocStatus,
    edPainLevel, setEdPainLevel,
    edBodyAreas, setEdBodyAreas,
    edSafetyFlags, setEdSafetyFlags,
    edCheckinVisible, setEdCheckinVisible,
    edCheckinComplete, setEdCheckinComplete,
    edCheckinSummary, setEdCheckinSummary,

    // Stage & recommendations
    edStage, setEdStage,
    checkinRecommendations, setCheckinRecommendations,

    // Functions
    loadEDHistory,
    startEDVisit,
    endEDVisit,
    logEDCheckin,
    clearEDCheckins,
    cycleEDMode,
    startEDTimer,
    stopEDTimer,
    toggleEdBodyArea,
    toggleSafetyFlag,
    submitEDCheckin,
    copyEDReport,
    downloadEDReportPDF,
    copyScript,
  };
}
